import { useCallback, useEffect, useState } from 'react';
import { api, ApiError } from '../api';
import { ErrorBox, StatusChip } from '../components/Layout';
import { laoDateTime } from '../lib/money';
import {
  NOTIFY_RESULT_LABEL,
  STATUS_LABEL,
  type BankNotification,
  type WebhookDelivery,
} from '../types';

export function LogsPage() {
  const [notifications, setNotifications] = useState<BankNotification[]>([]);
  const [deliveries, setDeliveries] = useState<WebhookDelivery[]>([]);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [n, w] = await Promise.all([api.notifications(), api.webhooks()]);
      setNotifications(n.notifications);
      setDeliveries(w.deliveries);
      setError(null);
    } catch (err: unknown) {
      setError(err instanceof ApiError ? err.message : 'ໂຫຼດຂໍ້ມູນບໍ່ໄດ້');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function retry(id: number) {
    try {
      await api.retryWebhook(id);
      await load();
    } catch (err: unknown) {
      setError(err instanceof ApiError ? err.message : 'ສົ່ງໃໝ່ບໍ່ສຳເລັດ');
    }
  }

  return (
    <>
      <ErrorBox error={error} />

      <section id="notifications">
        <h2 className="section-title">ແຈ້ງເຕືອນເງິນເຂົ້າຫຼ້າສຸດ</h2>
        {notifications.length === 0 ? (
          <p className="muted">ຍັງບໍ່ມີແຈ້ງເຕືອນຈາກມືຖື</p>
        ) : (
          <ul className="admin-orders">
            {notifications.map((n) => (
              <li className="card admin-order" key={n.id}>
                <div className="admin-order-head">
                  <span className="muted">{laoDateTime(n.createdAt)}</span>
                  <StatusChip
                    status={n.result === 'SUCCESS' ? 'paid' : 'failed'}
                    label={NOTIFY_RESULT_LABEL[n.result] ?? n.result}
                  />
                </div>
                <p className="mono">{n.message}</p>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section id="webhooks">
        <h2 className="section-title">Webhook ຫຼ້າສຸດ</h2>
        {deliveries.length === 0 ? (
          <p className="muted">ຍັງບໍ່ມີການສົ່ງ</p>
        ) : (
          <ul className="admin-orders">
            {deliveries.map((d) => (
              <li className="card admin-order" key={d.id}>
                <div className="admin-order-head">
                  <strong>
                    {d.event} · {d.paymentId}
                  </strong>
                  <StatusChip status={d.status} label={STATUS_LABEL[d.status] ?? d.status} />
                </div>
                <p className="muted">
                  {laoDateTime(d.createdAt)} · ພະຍາຍາມ {d.attempts} ຄັ້ງ
                </p>
                {d.lastError && <p className="alert">{d.lastError}</p>}
                {d.status === 'failed' && (
                  <button type="button" className="link-button" onClick={() => retry(d.id)}>
                    ສົ່ງໃໝ່
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>
    </>
  );
}
